import api from '../../API';
import { useLoaderData, useRevalidator, Link, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import OpenSeadragonViewer from '../../widgets/OpenSeadragon';
import { useAuth } from '../../providers/AuthContext';
import { queryClient } from '../../providers/AuthContext';
import { fetchApiQuery } from '../../utils/apiResponse';
import { getRegionDisplayLabel } from '../../utils/stainRegionFields';
import styles from './item.module.css';
import sharedStyles from './styles.module.css';
import { Centered } from '../../layouts/Base';

function Item() {
    const { itemId } = useParams();
    const { user, pending } = useAuth();
    const revalidator = useRevalidator();
    const hasRevalidated = useRef(false);
    const { ok, data, error } = useLoaderData();
    const [viewMode, setViewMode] = useState('detail'); // 'detail' | 'full'
    const [stainDetailsOpen, setStainDetailsOpen] = useState(false);
    const [regionsDetailsOpen, setRegionsDetailsOpen] = useState(false);

    useEffect(() => {
        if (!pending && user && user._id && !ok && !hasRevalidated.current) {
            hasRevalidated.current = true;
            revalidator.revalidate();
        }
    }, [user, pending, ok, revalidator]);

    useEffect(() => {
        if (ok) hasRevalidated.current = false;
    }, [ok]);

    if (pending || revalidator.state === 'loading' || (!ok && user && user._id && !hasRevalidated.current)) {
        return <div>Loading...</div>;
    }

    if (!ok) {
        return (
            <Centered>
                <h1>Oops, there was a problem</h1>
                <p>{error}</p>
            </Centered>
        );
    }

    const payload = data;
    const breadcrumb = payload.breadcrumb || [];
    const hasViewer = payload.hasLargeImage && payload.tileMetadata && payload.girderItemId;

    const viewerProps = {
        girderItemId: payload.girderItemId,
        tileMetadata: payload.tileMetadata,
        style: { width: '100%', height: '100%' },
    };

    if (viewMode === 'full') {
        return (
            <div className={styles.viewerFullscreen}>
                <div className={styles.viewerTopBar}>
                    <button type="button" className={styles.secondary} onClick={() => setViewMode('detail')}>
                        Back to details
                    </button>
                    <span className={styles.viewerTopBarTitle}>{payload.itemName || payload.networkFilename || 'Slide'}</span>
                </div>
                <div className={styles.viewerFullscreenContent}>
                    {hasViewer && <OpenSeadragonViewer {...viewerProps} />}
                </div>
            </div>
        );
    }

    const isBdsaItem = payload.isBdsaItem !== false && payload.bdsaItemId != null;

    const stainDisplayValue = payload.stain != null
        ? (typeof payload.stain === 'object' && payload.stain.name != null ? payload.stain.name : JSON.stringify(payload.stain))
        : null;

    const formatRegionName = (r) => {
        if (r == null || typeof r !== 'object') return '—';
        const abbr = r.abbreviation != null && String(r.abbreviation).trim() !== '' ? String(r.abbreviation) : null;
        const full = r.displayName != null && String(r.displayName).trim() !== '' ? String(r.displayName)
            : (r.name != null && String(r.name).trim() !== '' ? String(r.name) : null);
        if (full && abbr) return `${full} (${abbr})`;
        return full || abbr || '—';
    };

    const regionsDisplayValue = (payload.regions != null && payload.regions.length > 0)
        ? payload.regions.map((r) => formatRegionName(r)).join(', ')
        : null;

    const renderDetailsRows = (rows) => {
        const entries = (rows || []).filter((r) => r && r.value != null && String(r.value).trim() !== '');
        if (entries.length === 0) return <div className={styles.detailsList}>—</div>;
        return (
            <div className={styles.detailsList}>
                {entries.map((r) => (
                    <div className={styles.detailsRow} key={r.key}>
                        <div className={styles.detailsKey}>{r.label}</div>
                        <div className={styles.detailsVal}>{r.value}</div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className={styles.itemPage}>
            <nav className={sharedStyles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/collections">Collections</Link>
                {breadcrumb.map((b) => (
                    <span key={b.id}>
                        <span aria-hidden="true"> / </span>
                        {b.type === 'collection' ? (
                            <Link to={`/collection/${b.id}`}>{b.name}</Link>
                        ) : (
                            <Link to={`/folder/${b.id}`}>{b.name}</Link>
                        )}
                    </span>
                ))}
                <span aria-hidden="true"> / </span>
                <span>{payload.itemName || payload.networkFilename || 'Slide'}</span>
            </nav>
            <div className={styles.metadataPanel}>
                <h2>{payload.itemName || payload.networkFilename || 'Slide'}</h2>
                <div className={styles.metadataGrid}>
                    {isBdsaItem && payload.caseNetworkIdentifier != null && (
                        <>
                            <span className={styles.metadataLabel}>Case (network)</span>
                            <span>{payload.caseNetworkIdentifier}</span>
                        </>
                    )}
                    {isBdsaItem && payload.caseLocalIdentifier != null && (
                        <>
                            <span className={styles.metadataLabel}>Case (local)</span>
                            <span>{payload.caseLocalIdentifier}</span>
                        </>
                    )}
                    {isBdsaItem && payload.networkFilename != null && (
                        <>
                            <span className={styles.metadataLabel}>Network filename</span>
                            <span>{payload.networkFilename}</span>
                        </>
                    )}
                    {isBdsaItem && payload.stain != null && (
                        <>
                            <span className={styles.metadataLabel}>Stain</span>
                            <span className={styles.metadataValueWithDetails}>
                                <span className={styles.valueAndToggle}>
                                    {stainDisplayValue}
                                    <button
                                        type="button"
                                        className={styles.detailsToggle}
                                        onClick={() => setStainDetailsOpen((v) => !v)}
                                        aria-expanded={stainDetailsOpen}
                                        aria-controls="stain-details"
                                    >
                                        {stainDetailsOpen ? 'Hide details' : 'Details'}
                                    </button>
                                </span>
                                {stainDetailsOpen && (
                                    <div id="stain-details" className={styles.detailsBlock} role="region" aria-label="Stain full data">
                                        {renderDetailsRows([
                                            { key: 'vendor', label: 'Vendor', value: payload.stainDetails?.vendor },
                                            { key: 'dilution', label: 'Dilution', value: payload.stainDetails?.dilution },
                                            { key: 'antibody', label: 'Antibody', value: payload.stainDetails?.antibody },
                                            { key: 'phospho', label: 'Phospho-specific', value: payload.stainDetails?.phosphoSpecific },
                                            { key: 'chromogen', label: 'Chromogen', value: payload.stainDetails?.chromogen },
                                            { key: 'chemistry', label: 'Chemistry', value: payload.stainDetails?.chemistry },
                                        ])}
                                    </div>
                                )}
                            </span>
                        </>
                    )}
                    {isBdsaItem && payload.regions != null && payload.regions.length > 0 && (
                        <>
                            <span className={styles.metadataLabel}>Regions</span>
                            <span className={styles.metadataValueWithDetails}>
                                <span className={styles.valueAndToggle}>
                                    {regionsDisplayValue}
                                    <button
                                        type="button"
                                        className={styles.detailsToggle}
                                        onClick={() => setRegionsDetailsOpen((v) => !v)}
                                        aria-expanded={regionsDetailsOpen}
                                        aria-controls="regions-details"
                                    >
                                        {regionsDetailsOpen ? 'Hide details' : 'Details'}
                                    </button>
                                </span>
                                {regionsDetailsOpen && (
                                    <div id="regions-details" className={styles.detailsBlock} role="region" aria-label="Regions full data">
                                        <div className={styles.detailsList}>
                                            {payload.regions.map((r, idx) => {
                                                const title = formatRegionName(r);
                                                const d = r?.details || {};
                                                return (
                                                    <div key={`${title}-${idx}`} className={idx === 0 ? undefined : styles.detailsSubsection}>
                                                        <div className={styles.detailsSubsectionTitle}>{title}</div>
                                                        {renderDetailsRows([
                                                            { key: 'regionCategory', label: 'Category', value: d.regionCategory },
                                                            { key: 'hemisphere', label: 'Hemisphere', value: d.hemisphere ?? '—' },
                                                            { key: 'sliceOrientation', label: 'Slice orientation', value: d.sliceOrientation ?? '—' },
                                                            { key: 'landmarks', label: 'Landmarks', value: Array.isArray(d.landmarks) ? d.landmarks.join(', ') : null },
                                                            { key: 'damage', label: 'Damage', value: Array.isArray(d.damage) ? d.damage.join(', ') : null },
                                                        ])}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </span>
                        </>
                    )}
                    {isBdsaItem && payload.magnification != null && (
                        <>
                            <span className={styles.metadataLabel}>Magnification</span>
                            <span>{payload.magnification}</span>
                        </>
                    )}
                    {isBdsaItem && payload.level != null && (
                        <>
                            <span className={styles.metadataLabel}>Level</span>
                            <span>{payload.level}</span>
                        </>
                    )}
                    {isBdsaItem && payload.scan != null && (
                        <>
                            <span className={styles.metadataLabel}>Scan</span>
                            <span>{payload.scan}</span>
                        </>
                    )}
                    {isBdsaItem && payload.blockName != null && (
                        <>
                            <span className={styles.metadataLabel}>Block</span>
                            <span>{payload.blockName}</span>
                        </>
                    )}
                </div>
            </div>
            <div className={styles.toolbar}>
                <a href={`/dsa#item/${payload.girderItemId}`} target="_blank" rel="noopener noreferrer" className={styles.dsaLink}>
                    Open in DSA
                </a>
                {hasViewer && (
                    <>
                        <button type="button" onClick={() => setViewMode('full')}>
                            Expand viewer
                        </button>
                        <Link to={`/item/${itemId}/annotate`} className={styles.dsaLink}>
                            Annotate
                        </Link>
                    </>
                )}
            </div>
            {hasViewer ? (
                <div className={styles.viewerContainer}>
                    <OpenSeadragonViewer {...viewerProps} />
                </div>
            ) : (
                <div className={styles.noViewer}>No whole-slide image available for this item.</div>
            )}
        </div>
    );
}

/**
 * Loader: fetch unified BDSA item detail by Girder item id.
 */
async function Loader({ params }) {
    const { itemId } = params;
    try {
        const response = await fetchApiQuery(queryClient, {
            queryKey: ['bdsa-item-detail', itemId],
            queryFn: () => api.get(`bdsa/item/by_item/${itemId}`),
            staleTime: 5 * 60 * 1000,
        });
        if (response?.data) {
            return { ok: true, data: response.data, error: null };
        }
        return { ok: false, data: null, error: 'There was a problem loading the slide.' };
    } catch (err) {
        const msg = err?.message || '';
        const notFound = /\(HTTP 404\)/.test(msg);
        return {
            ok: false,
            data: null,
            error: notFound ? 'Item not found.' : (msg || 'There was a problem loading the slide.'),
        };
    }
}

export default Item;
export { Item, Loader };
